import {
  VinculoAssessorDefensorFormDto,
  VinculoDefensorAssessorFormDto,
} from '@/modules/people/presentation/person-form.utils';

/** Normaliza para yyyy-MM-dd (comparação lexicográfica segura para datas ISO). */
export function toDateKeyVinculo(value?: string | Date | null): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (!s) return null;
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

export type PeriodoVinculoAssessor = {
  colaborador_id?: number;
  data_inicial?: string | Date | null;
  data_final?: string | Date | null;
};

/**
 * Intervalos [inicio, fim] (fim nulo = em aberto) se sobrepõem.
 * Contato no mesmo dia conta como colisão.
 */
export function periodosVinculoAssessorColidem(
  a: PeriodoVinculoAssessor,
  b: PeriodoVinculoAssessor,
): boolean {
  const aIni = toDateKeyVinculo(a.data_inicial);
  const bIni = toDateKeyVinculo(b.data_inicial);
  if (!aIni || !bIni) return false;

  const aFim = toDateKeyVinculo(a.data_final) ?? '9999-12-31';
  const bFim = toDateKeyVinculo(b.data_final) ?? '9999-12-31';

  return aIni <= bFim && bIni <= aFim;
}

export function validarVinculoAssessorDefensor(
  candidato: PeriodoVinculoAssessor,
  existentes: PeriodoVinculoAssessor[],
  indiceEdicao = -1,
): string | null {
  if (!candidato.colaborador_id) {
    return 'Selecione o assessor.';
  }

  const inicio = toDateKeyVinculo(candidato.data_inicial);
  if (!inicio) {
    return 'Informe a data inicial.';
  }

  const fim = toDateKeyVinculo(candidato.data_final);
  if (fim && fim < inicio) {
    return 'A data final não pode ser anterior à data inicial.';
  }

  const colide = existentes.some((item, index) => {
    if (index === indiceEdicao) return false;
    if (Number(item.colaborador_id) !== Number(candidato.colaborador_id)) return false;
    if (!toDateKeyVinculo(item.data_inicial)) return false;
    return periodosVinculoAssessorColidem(candidato, item);
  });

  if (colide) {
    return 'O período informado colide com outro vínculo deste assessor neste defensor.';
  }

  return null;
}

export function validarListaVinculosAssessores(
  lista: VinculoAssessorDefensorFormDto[],
): string | null {
  for (let i = 0; i < lista.length; i++) {
    const erro = validarVinculoAssessorDefensor(lista[i], lista, i);
    if (erro) return erro;
  }
  return null;
}

/** Validação na visão inversa: um assessor não pode ter períodos sobrepostos, mesmo entre defensores distintos. */
export function validarVinculoDefensorAssessor(
  candidato: VinculoDefensorAssessorFormDto,
  existentes: VinculoDefensorAssessorFormDto[],
  indiceEdicao = -1,
): string | null {
  if (!candidato.defensor_id) return 'Selecione o defensor.';

  const inicio = toDateKeyVinculo(candidato.data_inicial);
  if (!inicio) return 'Informe a data inicial.';

  const fim = toDateKeyVinculo(candidato.data_final);
  if (fim && fim < inicio) return 'A data final não pode ser anterior à data inicial.';

  const colide = existentes.some((item, index) => {
    if (index === indiceEdicao || !toDateKeyVinculo(item.data_inicial)) return false;
    return periodosVinculoAssessorColidem(candidato, item);
  });

  return colide
    ? 'O assessor já possui vínculo com um defensor no período informado.'
    : null;
}

export function validarListaVinculosDefensores(
  lista: VinculoDefensorAssessorFormDto[],
): string | null {
  for (let i = 0; i < lista.length; i++) {
    const erro = validarVinculoDefensorAssessor(lista[i], lista, i);
    if (erro) return erro;
  }
  return null;
}
